import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Card, Icon, PageEyebrow } from '../../ui/index.js';
import { useAuthSession } from '../../auth/AuthContext.js';
import { useInfra } from '../../infra/context.js';
import { getAlbumKey } from '../albums/albumKeys.js';
import { decryptTagsCached, type DecryptedTag } from '../tags/tagCache.js';

interface Loaded {
  rootId: string;
  albumKey: CryptoKey;
  tags: DecryptedTag[];
}

// Viewer-mode list of tags for a shared root. Read-only — no create / rename
// / delete affordances. Same data shape as TagsListPage but resolves the
// album key from the incoming share rather than the owner's wrappedAlbumKey.
export function SharedTagsListPage() {
  const { rootId } = useParams<{ rootId: string }>();
  const session = useAuthSession();
  const { metadata } = useInfra();
  const [state, setState] = useState<Loaded | 'loading' | { error: string }>('loading');

  useEffect(() => {
    if (!rootId) return;
    let cancelled = false;
    setState('loading');
    (async () => {
      try {
        const { shares } = await metadata.listIncomingShares(session.jwt);
        const share = shares.find((s) => s.rootAlbumId === rootId);
        if (!share) {
          if (!cancelled) setState({ error: 'condivisione non trovata' });
          return;
        }
        const albumKey = await getAlbumKey(rootId, share.wrappedAlbumKey, session.rsaPriv);
        const { tags } = await metadata.listTags(rootId, session.jwt);
        const decrypted = await decryptTagsCached(tags, rootId, albumKey);
        if (!cancelled) setState({ rootId, albumKey, tags: decrypted });
      } catch (err) {
        if (!cancelled) setState({ error: err instanceof Error ? err.message : 'errore' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rootId, session.jwt, session.rsaPriv, metadata]);

  const sortedTags = useMemo(() => {
    if (typeof state !== 'object' || 'error' in state) return [] as DecryptedTag[];
    const collator = new Intl.Collator(undefined, { sensitivity: 'base' });
    return [...state.tags].sort((a, b) => collator.compare(a.name, b.name));
  }, [state]);

  if (!rootId) return null;
  if (state === 'loading') return <p className="py-6 text-sm text-on-surface-variant">Carico…</p>;
  if ('error' in state) return <p className="py-6 text-sm text-error">Errore: {state.error}</p>;

  return (
    <div className="flex flex-col gap-7">
      <div className="flex flex-col gap-3">
        <PageEyebrow>viewer · solo lettura</PageEyebrow>
        <Breadcrumb rootId={rootId} />
        <h2 className="font-display text-2xl font-semibold">
          Tag <span className="accent-text">condivisi</span>
        </h2>
        <p className="text-sm text-on-surface-variant">
          I tag sono cifrati con la chiave della radice condivisa con te. Solo il proprietario può
          crearli, rinominarli o cancellarli.
        </p>
      </div>

      <div className="flex flex-col gap-3">
        <h3 className="font-display text-lg font-semibold">
          {sortedTags.length === 0
            ? 'Nessun tag'
            : sortedTags.length === 1
              ? '1 tag'
              : `${sortedTags.length} tag`}
        </h3>
        {sortedTags.length === 0 ? (
          <Card className="flex flex-col items-center gap-2 py-8 text-center">
            <Icon name="sell" className="text-3xl text-on-surface-variant" />
            <p className="text-sm text-on-surface-variant">
              Nessun tag in questa radice condivisa.
            </p>
          </Card>
        ) : (
          <div className="flex flex-col gap-2">
            {sortedTags.map((dt) => (
              <Link
                key={dt.tag.id}
                to={`/shared/${encodeURIComponent(rootId)}/tags/${encodeURIComponent(dt.tag.id)}`}
                className="block"
              >
                <Card className="flex items-center gap-3 transition-colors hover:border-cyan/60">
                  <Icon name="sell" className="text-xl text-cyan" />
                  <span className="flex-1 font-display text-base font-semibold">{dt.name}</span>
                  <Icon name="arrow_forward" className="text-base text-on-surface-variant" />
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Breadcrumb({ rootId }: { rootId: string }) {
  return (
    <nav className="flex flex-wrap items-center gap-2 text-sm">
      <Link to="/shared" className="text-on-surface-variant transition-colors hover:text-cyan">
        Condivisi
      </Link>
      <Icon name="chevron_right" className="text-base text-on-surface-variant" />
      <Link
        to={`/shared/${encodeURIComponent(rootId)}`}
        className="text-on-surface-variant transition-colors hover:text-cyan"
      >
        Radice
      </Link>
      <Icon name="chevron_right" className="text-base text-on-surface-variant" />
      <span className="font-display text-base font-semibold text-cyan">Tag</span>
    </nav>
  );
}
